import { AUDIT_ENDPOINTS } from 'constants/apiEndPoints';
import ThrottledApi from './api/throttledApi';
import { AxiosResponse } from 'axios';
import moment from 'moment';
import { AuditDetailModel, DailyReportModel, AuditsByDateModel } from 'models';
import { AuditRow, BackstockLedgerResponse, StoreAuditSummariesResponse } from 'features/audits/types';
import { getAPIBaseUrl } from 'utils';

export interface StoreManualDayEntry {
  id: string;
  store_id: string;
  business_date: string;
  lotto_sales_amount?: number | null;
  lotto_payouts_amount?: number | null;
  cash_collection_amount?: number | null;
  credit_card_collection_amount?: number | null;
  entered_by?: string | null;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface StoreManualDayEntryUpsertPayload {
  business_date: string;
  lotto_sales_amount?: number | null;
  lotto_payouts_amount?: number | null;
  cash_collection_amount?: number | null;
  credit_card_collection_amount?: number | null;
}

export interface StorePosManualDayEntryUpsertPayload {
  business_date: string;
  scratcher_qty?: number | null;
  scratcher_sales?: number | null;
  lotto_sales?: number | null;
  payout?: number | null;
}

export type AuditReportPackScopeMetric =
  | 'opening'
  | 'activated'
  | 'closing'
  | 'moved_to_backstock'
  | 'counter_sold_out'
  | 'backstock_opening'
  | 'backstock_activated_to_backstock'
  | 'backstock_moved_from_counter'
  | 'backstock_returned_to_counter'
  | 'backstock_returned_to_lottery'
  | 'backstock_inventory_depletion'
  | 'backstock_closing'
  | 'vending_opening'
  | 'vending_activated'
  | 'vending_moved'
  | 'vending_sold_out'
  | 'vending_closing';

export interface AuditReportPackScopeParams {
  metric: AuditReportPackScopeMetric;
  report_date?: string;
  start_date?: string;
  end_date?: string;
  audit_id?: string;
}

export interface AuditReportPackScopeResponse {
  store_id: string;
  metric: AuditReportPackScopeMetric;
  pack_count: number;
  packs: Array<{
    game_id: string;
    pack_id: string;
  }>;
  report_date?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  audit_id?: string | null;
}

const mapShiftActivityResponseRow = (activity: any) => ({
  ...activity,
  operator_name: activity?.operator_name || activity?.created_by_name || null,
});

export const mapReconciliationAuditResponseRow = (audit: any) => {
  const displayDatetime = audit?.report_datetime || audit?.audit_date || audit?.audit_created_at || null;

  return {
    ...audit,
    report_datetime: displayDatetime,
    audit_date: displayDatetime,
    shift_activity: Array.isArray(audit?.shift_activity)
      ? audit.shift_activity.map(mapShiftActivityResponseRow)
      : [],
  };
};

const toNullableNumber = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null;
  }

  const nextValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(nextValue) ? nextValue : null;
};

// ===== GET OPERATIONS =====

const buildPosSummaryFromTotals = (totals: any) => {
  const pos = totals?.pos;
  const posAuditWindow = totals?.pos_audit_window;

  if (!pos && !posAuditWindow) {
    return undefined;
  }

  const auditCount = Number(totals?.audit?.audit_count ?? 0);
  const scratcherTotals = auditCount > 0 || !pos ? posAuditWindow : pos;

  return {
    scratcher_quantity_sold: Number(scratcherTotals?.scratcher_count ?? 0),
    scratcher_sales: Number(scratcherTotals?.scratcher_sales ?? 0),
    lotto_sales: Number(pos?.lotto_sales ?? 0),
    payout: Number(pos?.payout ?? 0),
  };
};

// Get store audit summaries (returns raw data structure for backward compatibility)

export const getStoreAuditSummaries = async (storeId: string, startDate: string, endDate: string) => {
  try {
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');

    const response = await ThrottledApi.get(
      AUDIT_ENDPOINTS.GET_STORE_AUDIT_SUMMARIES(storeId, formattedStartDate, formattedEndDate)
    ) as AxiosResponse;

    if (response.data?.daily_summaries) {
      return response.data.daily_summaries.map((dailySummary: any) => {
        const { report_date, audits = [], daily_totals, subscription_status, verified, audit_missing } = dailySummary;

        // Prefer backend-provided audit_count; fallback to audits array length.
        const audit_count = Number(daily_totals?.audit?.audit_count ?? (Array.isArray(audits) ? audits.length : 0));

        // Transform daily_totals.audit → audit_summary
        const audit_summary = daily_totals?.audit ? {
          audit_count,
          total_sold: Number(daily_totals.audit.total_sold ?? 0),
          revenue: Number(daily_totals.audit.total_revenue ?? 0),
          total_packs: Number(daily_totals.audit.total_packs ?? 0),
          games_count: Number(daily_totals.audit.total_games ?? 0),
          newly_activated_count: Number(daily_totals.audit.newly_activated_count ?? 0),
          newly_activated_pack_count: Number(daily_totals.audit.newly_activated_pack_count ?? 0),
          newly_activated_value: Number(daily_totals.audit.newly_activated_value ?? 0),
          opening_count: Number(daily_totals.audit.opening_count ?? 0),
          opening_pack_count: Number(daily_totals.audit.opening_pack_count ?? 0),
          closing_count: Number(daily_totals.audit.closing_count ?? 0),
          closing_pack_count: Number(daily_totals.audit.closing_pack_count ?? 0),
          counter_sold_out_pack_count: Number(daily_totals.audit.counter_sold_out_pack_count ?? 0),
          moved_to_backstock_count: Number(daily_totals.audit.moved_to_backstock_count ?? 0),
          moved_to_backstock_ticket_count: Number(daily_totals.audit.moved_to_backstock_ticket_count ?? 0),
          opening_value: Number(daily_totals.audit.opening_value ?? 0),
          closing_value: Number(daily_totals.audit.closing_value ?? 0),
        } : undefined;

        const pos_summary = buildPosSummaryFromTotals(daily_totals);

        // Transform daily_totals.lotto → lotto_summary
        const lotto_summary = daily_totals?.lotto ? {
          sales: Number(daily_totals.lotto.sales ?? 0),
          payouts_total: Number(daily_totals.lotto.payouts_total ?? 0),
          payouts_count: Number(daily_totals.lotto.payouts_count ?? 0),
          lotto_draw_sales_amount: Number(daily_totals.lotto.lotto_draw_sales_amount ?? 0),
          lotto_voucher_issued_amount: Number(daily_totals.lotto.lotto_voucher_issued_amount ?? 0),
          lotto_gift_cards_issued_amount: Number(daily_totals.lotto.lotto_gift_cards_issued_amount ?? 0),
          lotto_draw_payouts_amount: Number(daily_totals.lotto.lotto_draw_payouts_amount ?? 0),
          lotto_voucher_redeemed_amount: Number(daily_totals.lotto.lotto_voucher_redeemed_amount ?? 0),
          lotto_card_trans_redeemed_amount: Number(daily_totals.lotto.lotto_card_trans_redeemed_amount ?? 0),
          lotto_payouts_amount: Number(daily_totals.lotto.lotto_payouts_amount ?? 0),
          scratcher_payouts_amount: Number(daily_totals.lotto.scratcher_payouts_amount ?? 0),
          activated_count: Number(daily_totals.lotto.activated_count ?? 0),
          activated_pack_count: Number(daily_totals.lotto.activated_pack_count ?? 0),
          return_count: Number(daily_totals.lotto.return_count ?? 0),
          return_value_total: Number(daily_totals.lotto.return_value_total ?? 0),
          scratcher_free_tickets_paid_count: Number(daily_totals.lotto.scratcher_free_tickets_paid_count ?? 0),
          scratcher_free_tickets_paid_amount: Number(daily_totals.lotto.scratcher_free_tickets_paid_amount ?? 0),
        } : undefined;

        const reconciliation_variance = daily_totals?.reconciliation_variance ? {
          scratcher: daily_totals.reconciliation_variance.scratcher ? {
            quantity_variance: toNullableNumber(daily_totals.reconciliation_variance.scratcher.quantity_variance),
            revenue_variance: toNullableNumber(daily_totals.reconciliation_variance.scratcher.revenue_variance),
          } : undefined,
          lotto_sales_variance: toNullableNumber(daily_totals.reconciliation_variance.lotto_sales_variance),
          payout_variance: toNullableNumber(daily_totals.reconciliation_variance.payout_variance),
          total_variance: toNullableNumber(daily_totals.reconciliation_variance.total_variance),
        } : undefined;

        // ✅ Include audits array and verified object in final object
        return {
          report_date,
          audits, // full list of audits for that date
          audit_summary,
          pos_summary,
          lotto_summary,
          reconciliation_variance,
          subscription_status,
          verified, // verified object with is_verified, date_time, user_id, user_name
          audit_missing: Boolean(audit_missing),
        };
      });
    }

    return [];
  } catch (error: any) {
    console.error('AuditService: Error getting store audit summaries:', error);
    return [];
  }
};

const mapTotalsToAuditRow = (
  totals: any,
  reportDate: string,
  audits: any[] = [],
  options?: {
    subscription_status?: any;
    verified?: any;
    audit_missing?: boolean;
  }
): AuditRow => {
  const audit_count = Number(totals?.audit?.audit_count ?? (Array.isArray(audits) ? audits.length : 0));

  const audit_summary = totals?.audit ? {
    audit_count,
    total_sold: Number(totals.audit.total_sold ?? 0),
    revenue: Number(totals.audit.total_revenue ?? 0),
    total_packs: Number(totals.audit.total_packs ?? 0),
    games_count: Number(totals.audit.total_games ?? 0),
    newly_activated_count: Number(totals.audit.newly_activated_count ?? 0),
    newly_activated_pack_count: Number(totals.audit.newly_activated_pack_count ?? 0),
    newly_activated_value: Number(totals.audit.newly_activated_value ?? 0),
    opening_count: Number(totals.audit.opening_count ?? 0),
    opening_pack_count: Number(totals.audit.opening_pack_count ?? 0),
    closing_count: Number(totals.audit.closing_count ?? 0),
    closing_pack_count: Number(totals.audit.closing_pack_count ?? 0),
    counter_sold_out_pack_count: Number(totals.audit.counter_sold_out_pack_count ?? 0),
    moved_to_backstock_count: Number(totals.audit.moved_to_backstock_count ?? 0),
    moved_to_backstock_ticket_count: Number(totals.audit.moved_to_backstock_ticket_count ?? 0),
    opening_value: Number(totals.audit.opening_value ?? 0),
    closing_value: Number(totals.audit.closing_value ?? 0),
  } : undefined;

  const pos_summary = buildPosSummaryFromTotals(totals);
  const pos_audit_window = totals?.pos_audit_window
    ? {
        scratcher_count: Number(totals.pos_audit_window.scratcher_count ?? 0),
        scratcher_sales: Number(totals.pos_audit_window.scratcher_sales ?? 0),
      }
    : undefined;

  const lotto_summary = totals?.lotto ? {
    sales: Number(totals.lotto.sales ?? 0),
    payouts_total: Number(totals.lotto.payouts_total ?? 0),
    payouts_count: Number(totals.lotto.payouts_count ?? 0),
    lotto_draw_sales_amount: Number(totals.lotto.lotto_draw_sales_amount ?? 0),
    lotto_voucher_issued_amount: Number(totals.lotto.lotto_voucher_issued_amount ?? 0),
    lotto_gift_cards_issued_amount: Number(totals.lotto.lotto_gift_cards_issued_amount ?? 0),
    lotto_draw_payouts_amount: Number(totals.lotto.lotto_draw_payouts_amount ?? 0),
    lotto_voucher_redeemed_amount: Number(totals.lotto.lotto_voucher_redeemed_amount ?? 0),
    lotto_card_trans_redeemed_amount: Number(totals.lotto.lotto_card_trans_redeemed_amount ?? 0),
    lotto_payouts_amount: Number(totals.lotto.lotto_payouts_amount ?? 0),
    scratcher_payouts_amount: Number(totals.lotto.scratcher_payouts_amount ?? 0),
    activated_count: Number(totals.lotto.activated_count ?? 0),
    activated_pack_count: Number(totals.lotto.activated_pack_count ?? 0),
    return_count: Number(totals.lotto.return_count ?? 0),
    return_value_total: Number(totals.lotto.return_value_total ?? 0),
    scratcher_free_tickets_paid_count: Number(totals.lotto.scratcher_free_tickets_paid_count ?? 0),
    scratcher_free_tickets_paid_amount: Number(totals.lotto.scratcher_free_tickets_paid_amount ?? 0),
  } : undefined;

  const reconciliation_variance = totals?.reconciliation_variance ? {
    scratcher: totals.reconciliation_variance.scratcher ? {
      quantity_variance: toNullableNumber(totals.reconciliation_variance.scratcher.quantity_variance),
      revenue_variance: toNullableNumber(totals.reconciliation_variance.scratcher.revenue_variance),
    } : undefined,
    lotto_sales_variance: toNullableNumber(totals.reconciliation_variance.lotto_sales_variance),
    payout_variance: toNullableNumber(totals.reconciliation_variance.payout_variance),
    total_variance: toNullableNumber(totals.reconciliation_variance.total_variance),
  } : undefined;

  return {
    report_date: reportDate,
    audits,
    audit_summary,
    pos_summary,
    pos_audit_window,
    lotto_summary,
    reconciliation_variance,
    total_amount: 0,
    subscription_status: options?.subscription_status,
    verified: options?.verified,
    audit_missing: Boolean(options?.audit_missing),
  } as AuditRow;
};

export const getStoreAuditSummariesWithOverallTotals = async (
  storeId: string,
  startDate: string,
  endDate: string
): Promise<StoreAuditSummariesResponse> => {
  try {
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
    const response = await ThrottledApi.get(
      AUDIT_ENDPOINTS.GET_STORE_RECONCILIATION_DAILY_SUMMARY_RANGE(storeId, formattedStartDate, formattedEndDate)
    ) as AxiosResponse;

    if (!response.data?.daily_summaries) {
      return {
        dailySummaries: [],
        overallTotals: null,
      };
    }

    const dailySummaries = response.data.daily_summaries.map((dailySummary: any) => {
      const { report_date, audits = [], daily_totals, subscription_status, verified, audit_missing } = dailySummary;

      return mapTotalsToAuditRow(daily_totals, report_date, audits, {
        subscription_status,
        verified,
        audit_missing,
      });
    });

    return {
      storeId: response.data.store_id,
      storeName: response.data.store_name ?? null,
      storeTimezone: response.data.store_timezone ?? null,
      startDate: response.data.start_date,
      endDate: response.data.end_date,
      dailySummaries,
      overallTotals: response.data?.overall_totals
        ? mapTotalsToAuditRow(response.data.overall_totals, 'overall_totals')
        : null,
    };
  } catch (error: any) {
    console.error('AuditService: Error getting store audit summaries with overall totals:', error);
    return {
      dailySummaries: [],
      overallTotals: null,
    };
  }
};

export const getStoreReconciliationAuditsByDate = async (
  storeId: string,
  reportDate: string
): Promise<any[]> => {
  try {
    const formattedDate = moment(reportDate).format('YYYY-MM-DD');
    const response = await ThrottledApi.get(
      AUDIT_ENDPOINTS.GET_STORE_RECONCILIATION_AUDITS_BY_DATE(storeId, formattedDate)
    ) as AxiosResponse;

    const audits = response.data?.audits;
    if (!Array.isArray(audits)) {
      return [];
    }

    return audits.map(mapReconciliationAuditResponseRow);
  } catch (error: any) {
    console.error('AuditService: Error getting per-audit reconciliation rows:', error);
    return [];
  }
};

export const getStoreScratcherReconciliationAuditsByRange = async (
  storeId: string,
  startDate: string,
  endDate: string
): Promise<any[]> => {
  try {
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
    const response = await ThrottledApi.get(
      AUDIT_ENDPOINTS.GET_STORE_SCRATCHER_RECONCILIATION_AUDITS_RANGE(storeId, formattedStartDate, formattedEndDate)
    ) as AxiosResponse;

    const audits = response.data?.audits;
    if (!Array.isArray(audits)) {
      return [];
    }

    return audits.map(mapReconciliationAuditResponseRow);
  } catch (error: any) {
    console.error('AuditService: Error getting scratcher per-audit reconciliation rows:', error);
    return [];
  }
};

export const getStoreBackstockLedger = async (
  storeId: string,
  startDate: string,
  endDate: string
): Promise<BackstockLedgerResponse | null> => {
  try {
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
    const response = await ThrottledApi.get(
      AUDIT_ENDPOINTS.GET_STORE_BACKSTOCK_LEDGER(storeId, formattedStartDate, formattedEndDate)
    ) as AxiosResponse<BackstockLedgerResponse>;

    return response.data ?? null;
  } catch (error: any) {
    console.error('AuditService: Error getting store backstock ledger:', error);
    return null;
  }
};

export const getAuditReportPackScope = async (
  storeId: string,
  params: AuditReportPackScopeParams
): Promise<AuditReportPackScopeResponse | null> => {
  try {
    const searchParams = new URLSearchParams();
    searchParams.set('metric', params.metric);
    if (params.report_date) {
      searchParams.set('report_date', moment(params.report_date).format('YYYY-MM-DD'));
    }
    if (params.start_date) {
      searchParams.set('start_date', moment(params.start_date).format('YYYY-MM-DD'));
    }
    if (params.end_date) {
      searchParams.set('end_date', moment(params.end_date).format('YYYY-MM-DD'));
    }
    if (params.audit_id) {
      searchParams.set('audit_id', params.audit_id);
    }

    const response = await ThrottledApi.get(
      `${AUDIT_ENDPOINTS.GET_AUDIT_REPORT_PACK_SCOPE(storeId)}?${searchParams.toString()}`
    ) as AxiosResponse<AuditReportPackScopeResponse>;

    return response.data ?? null;
  } catch (error: any) {
    console.error('AuditService: Error getting audit report pack scope:', error);
    return null;
  }
};

// Get audit details (returns AuditDetailModel)
export const getAuditDetails = async (
  storeId: string,
  auditId: string
): Promise<AuditDetailModel | null> => {
  try {
    const response = await ThrottledApi.get(AUDIT_ENDPOINTS.GET_AUDIT_DETAILS(storeId, auditId)) as AxiosResponse;
    if (response.data) {
      return AuditDetailModel.fromServerData(response.data);
    }
    return null;
  } catch (error: any) {
    console.error('AuditService: Error getting audit details:', error);
    return null;
  }
};

// Get daily expanded report (returns DailyReportModel)
export const getDailyExpandedReport = async (
  storeId: string,
  startDate: string,
  endDate?: string
): Promise<DailyReportModel | null> => {
  try {
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate ?? startDate).format('YYYY-MM-DD');
    const response = await ThrottledApi.get(
      AUDIT_ENDPOINTS.GET_DAILY_EXPANDED_REPORT(storeId, formattedStartDate, formattedEndDate)
    ) as AxiosResponse;

    if (response.data) {
      return DailyReportModel.fromServerData(response.data);
    }

    return null;
  } catch (error: any) {
    console.error('AuditService: Error getting daily expanded report:', error);
    return null;
  }
};

export const getStoreManualDayEntry = async (storeId: string, businessDate: string): Promise<StoreManualDayEntry | null> => {
  try {
    const formattedDate = moment(businessDate).format('YYYY-MM-DD');
    const response = await ThrottledApi.get(
      AUDIT_ENDPOINTS.GET_STORE_MANUAL_DAY_ENTRY(storeId, formattedDate)
    ) as AxiosResponse;

    return response.data ?? null;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }

    console.error('AuditService: Error getting manual day entry:', error);
    throw error;
  }
};

export const upsertStoreManualDayEntry = async (
  storeId: string,
  payload: StoreManualDayEntryUpsertPayload
): Promise<StoreManualDayEntry> => {
  const response = await ThrottledApi.put(
    AUDIT_ENDPOINTS.UPSERT_STORE_MANUAL_DAY_ENTRY(storeId),
    payload
  ) as AxiosResponse;

  return response.data;
};

export const upsertStorePosManualDayEntry = async (
  storeId: string,
  payload: StorePosManualDayEntryUpsertPayload
) => {
  const response = await ThrottledApi.put(
    AUDIT_ENDPOINTS.UPSERT_STORE_POS_MANUAL_DAY_ENTRY(storeId),
    payload
  ) as AxiosResponse;

  return response.data;
};

export const deleteStoreManualDayEntry = async (storeId: string, businessDate: string) => {
  const formattedDate = moment(businessDate).format('YYYY-MM-DD');
  try {
    const response = await ThrottledApi.delete(
      AUDIT_ENDPOINTS.DELETE_STORE_MANUAL_DAY_ENTRY(storeId, formattedDate)
    ) as AxiosResponse;

    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }

    throw error;
  }
};


// ===== POST/UPDATE OPERATIONS (ACTUALLY USED) =====

// Resolve audit discrepancy - exists in service but not actively used in components yet
export const resolveDiscrepancy = async (storeId: string, auditId: string, discrepancyId: string, resolutionData: any) => {
  try {
    const response = await ThrottledApi.post(AUDIT_ENDPOINTS.RESOLVE_DISCREPANCY(storeId, auditId, discrepancyId), resolutionData) as AxiosResponse;

    if (response.data) {
      return {
        success: true,
        data: response.data,
        error: null
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received'
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

export const getAuditReceipt = async (auditId: string) => {
  try {
    const response = await ThrottledApi.get(AUDIT_ENDPOINTS.GET_AUDIT_RECEIPT(auditId)) as AxiosResponse;
    return response.data;
  } catch (error: any) {
    console.error('AuditService: Error getting audit receipts:', error);
    return null;
  }
};

export const getAuditReceiptImage = async (id: string) => {
  try {
    const response = await ThrottledApi.get(AUDIT_ENDPOINTS.GET_AUDIT_RECEIPT_IMAGE(id), {
      responseType: 'blob'
    }) as AxiosResponse;
    return response.data;
  } catch (error: any) {
    console.error('AuditService: Error getting audit receipts:', error);
    return null;
  }
};

const parseContentDispositionFilename = (contentDisposition: unknown): string | null => {
  const header = typeof contentDisposition === 'string' ? contentDisposition : '';
  const encodedMatch = header.match(/filename\*=UTF-8''([^;]+)/i);

  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return encodedMatch[1].trim().replace(/^"|"$/g, '');
    }
  }

  const filenameMatch = header.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1]?.trim() || null;
};

export interface AuditReceiptFile {
  blob: Blob;
  filename: string | null;
  contentType: string | null;
}

export const getAuditReceiptFile = async (id: string): Promise<AuditReceiptFile | null> => {
  try {
    const response = await ThrottledApi.get(AUDIT_ENDPOINTS.GET_AUDIT_RECEIPT_IMAGE(id), {
      responseType: 'blob'
    }) as AxiosResponse<Blob>;
    const contentTypeHeader = response.headers?.['content-type'];

    return {
      blob: response.data,
      filename: parseContentDispositionFilename(response.headers?.['content-disposition']),
      contentType: typeof contentTypeHeader === 'string'
        ? contentTypeHeader
        : response.data?.type || null,
    };
  } catch (error: any) {
    console.error('AuditService: Error getting audit receipt file:', error);
    return null;
  }
};

export const getAuditVideo = async (auditId: string): Promise<Blob | null> => {
  try {
    const response = await ThrottledApi.get(AUDIT_ENDPOINTS.GET_AUDIT_VIDEO(auditId), {
      responseType: 'blob'
    }) as AxiosResponse;
    return response.data;
  } catch (error: any) {
    console.error('AuditService: Error getting audit video:', error);
    return null;
  }
};

export interface AuditVideoPlaybackUrlResponse {
  url: string;
  expires_in_seconds: number;
  has_video: boolean;
}

const resolveAuditVideoPlaybackUrl = (url: string): string => {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  const apiBaseUrl = getAPIBaseUrl()?.replace(/\/+$/, '');
  if (!apiBaseUrl) {
    return url;
  }

  const normalizedPath = url.startsWith('/api/') ? url.slice('/api'.length) : url;
  return `${apiBaseUrl}${normalizedPath.startsWith('/') ? '' : '/'}${normalizedPath}`;
};

export const getAuditVideoPlaybackUrl = async (auditId: string): Promise<string | null> => {
  try {
    const response = await ThrottledApi.get(
      AUDIT_ENDPOINTS.GET_AUDIT_VIDEO_PLAYBACK_URL(auditId)
    ) as AxiosResponse<AuditVideoPlaybackUrlResponse>;
    const url = response.data?.url;
    return url ? resolveAuditVideoPlaybackUrl(url) : null;
  } catch (error: any) {
    console.error('AuditService: Error getting audit video playback URL:', error);
    return null;
  }
};

// Get audits complete through date for a store
export const getAuditsCompleteThroughDate = async (storeId: string) => {
  try {
    const response = await ThrottledApi.get(AUDIT_ENDPOINTS.GET_AUDITS_COMPLETE_THROUGH_DATE(storeId)) as AxiosResponse;
    
    if (response.data) {
      return {
        success: true,
        data: response.data,
        error: null
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received'
    };
  } catch (error: any) {
    console.error('AuditService: Error getting audits complete through date:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

// Get audits by date for a store
export const getAuditsByDate = async (storeId: string, date: string): Promise<AuditsByDateModel | null> => {
  try {
    const formattedDate = moment(date).format('YYYY-MM-DD');
    const response = await ThrottledApi.get(
      AUDIT_ENDPOINTS.GET_AUDITS_BY_DATE(storeId, formattedDate)
    ) as AxiosResponse;

    if (response.data) {
      return AuditsByDateModel.fromServerData(response.data);
    }

    return AuditsByDateModel.createEmpty();
  } catch (error: any) {
    console.error('AuditService: Error getting audits by date:', error);
    
    // Check if this is a "No audit found" error
    const errorMessage = error?.response?.data?.detail || error?.response?.data?.message || '';
    if (errorMessage.includes('No audit found')) {
      // Re-throw the error so React Query can catch it
      throw error;
    }
    
    return AuditsByDateModel.createEmpty();
  }
};

export const getAuditDashboardReceiptsByDate = async (storeId: string, date: string): Promise<unknown> => {
  try {
    const formattedDate = moment(date).format('YYYY-MM-DD');
    const response = await ThrottledApi.get(
      AUDIT_ENDPOINTS.GET_AUDITS_BY_DATE(storeId, formattedDate)
    ) as AxiosResponse;

    return response.data ?? null;
  } catch (error: any) {
    console.error('AuditService: Error getting audit dashboard receipts by date:', error);
    return null;
  }
};
